import React from 'react';
import { Plus, Copy, Check, ShieldCheck, Hammer, ScrollText, Pencil, CircleCheck, CircleOff, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@hts/ui';
import { useGet, apiPost, apiPut } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { relativeTime } from '../../lib/format.js';
import {
  copyToClipboard,
  PROBLEM_CLASS_LABELS,
  PROBLEM_CLASSES,
  type ScriptClassification,
  type ScriptLibraryEntry,
  type ScriptLibraryListResponse,
  type ScriptStatus,
} from '../../api/script-library.js';

/**
 * Admin > Script Library (issue #86 phase 1).
 *
 * Curated, reviewed scripts technicians copy into NinjaOne RMM themselves.
 * Editing an entry saves it as a NEW version and returns it to draft pending
 * re-approval; nothing is usable until approved. All mutations are audited
 * server-side.
 */

interface FormState {
  id?: string;
  name: string;
  problemClass: string;
  description: string;
  classification: ScriptClassification;
  language: string;
  body: string;
  parametersJson: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  problemClass: 'disk_space',
  description: '',
  classification: 'read_only',
  language: 'powershell',
  body: '',
  parametersJson: '[]',
};

function StatusBadge({ status }: { status: ScriptStatus }) {
  const styles: Record<ScriptStatus, string> = {
    approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    draft: 'bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300',
    disabled: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', styles[status])} data-testid={`status-${status}`}>
      {status}
    </span>
  );
}

export function ClassificationBadge({ classification }: { classification: ScriptClassification }) {
  return classification === 'read_only' ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
      <ShieldCheck className="h-3 w-3" /> read-only
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
      <Hammer className="h-3 w-3" /> remediation
    </span>
  );
}

export function ScriptLibraryPage() {
  const auth = useAuth();
  const role = auth.status === 'authenticated'
    ? auth.user.memberships.find(m => m.tenantId === auth.user.activeTenantId)?.role
    : undefined;
  const canEdit = role === 'admin' || role === 'owner';

  const [classFilter, setClassFilter] = React.useState<string>('');
  const [statusFilter, setStatusFilter] = React.useState<string>('');
  const params = new URLSearchParams();
  if (classFilter) params.set('problemClass', classFilter);
  if (statusFilter) params.set('status', statusFilter);
  const qs = params.toString();
  const { data, loading, error, refetch } = useGet<ScriptLibraryListResponse>(
    `/api/v1/script-library${qs ? `?${qs}` : ''}`,
    [qs],
  );

  const [openId, setOpenId] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<FormState | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const items = data?.items ?? [];

  async function copy(id: string, body: string) {
    const ok = await copyToClipboard(body);
    if (ok) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(c => (c === id ? null : c)), 2000);
      toast.success('Script copied');
    } else {
      toast.error('Could not copy to clipboard');
    }
  }

  function startEdit(entry: ScriptLibraryEntry) {
    setForm({
      id: entry.id,
      name: entry.name,
      problemClass: entry.problemClass,
      description: entry.description,
      classification: entry.classification,
      language: entry.language,
      body: entry.body,
      parametersJson: JSON.stringify(entry.parameters ?? [], null, 2),
    });
  }

  async function saveForm() {
    if (!form) return;
    let parameters: unknown[];
    try {
      const parsed = JSON.parse(form.parametersJson || '[]');
      if (!Array.isArray(parsed)) throw new Error('not an array');
      parameters = parsed;
    } catch {
      toast.error('Parameters must be a JSON array, e.g. [{"name":"user","description":"Affected user"}]');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        problemClass: form.problemClass,
        description: form.description.trim(),
        classification: form.classification,
        language: form.language.trim() || 'powershell',
        body: form.body,
        parameters,
      };
      if (form.id) {
        await apiPut(`/api/v1/script-library/${form.id}`, payload);
        toast.success('Saved as a new version - status reset to draft pending re-approval');
      } else {
        await apiPost('/api/v1/script-library', payload);
        toast.success('Created as draft - approve it before technicians can use it');
      }
      setForm(null);
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function transition(entry: ScriptLibraryEntry, action: 'approve' | 'disable') {
    setBusyId(entry.id);
    try {
      await apiPost(`/api/v1/script-library/${entry.id}/${action}`, {});
      toast.success(action === 'approve' ? `"${entry.name}" approved` : `"${entry.name}" disabled`);
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `Could not ${action}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-[hsl(var(--content))]">
            <ScrollText className="h-5 w-5 text-brand-500" /> Script Library
          </h1>
          <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5 max-w-2xl">
            Reviewed diagnostic and remediation scripts. Technicians copy them from the ticket
            into NinjaOne RMM themselves - the technician makes the call. Edits create a new
            version that must be re-approved before use.
            {!canEdit && auth.status === 'authenticated' && ' Read-only: admin or owner role required to change the library.'}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setForm({ ...EMPTY_FORM })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white"
          >
            <Plus className="h-4 w-4" /> New script
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-medium text-[hsl(var(--content-subtle))]">
          Problem class{' '}
          <select
            value={classFilter}
            onChange={e => setClassFilter(e.target.value)}
            className="ml-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1.5 text-sm"
            aria-label="Filter by problem class"
          >
            <option value="">All</option>
            {PROBLEM_CLASSES.map(c => (
              <option key={c} value={c}>{PROBLEM_CLASS_LABELS[c]}</option>
            ))}
          </select>
        </label>
        {canEdit && (
          <label className="text-xs font-medium text-[hsl(var(--content-subtle))]">
            Status{' '}
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="ml-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1.5 text-sm"
              aria-label="Filter by status"
            >
              <option value="">All</option>
              <option value="approved">Approved</option>
              <option value="draft">Draft</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
        )}
      </div>

      {loading && !data ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={4} />)}</div>
      ) : error ? (
        <ErrorState message={`Could not load the script library: ${error}`} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No scripts found"
          description={classFilter || statusFilter ? 'No entries match these filters.' : 'The library is empty. Seed it (pnpm --filter @hts/db db:seed) or add a script.'}
        />
      ) : (
        <div className="space-y-3" data-testid="script-list">
          {items.map(entry => (
            <article
              key={entry.id}
              className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4"
              data-testid={`script-row-${entry.problemClass}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">{entry.name}</span>
                    <ClassificationBadge classification={entry.classification} />
                    <StatusBadge status={entry.status} />
                    <span className="rounded-full bg-[hsl(var(--surface-subtle))] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--content-subtle))]">
                      v{entry.version}
                    </span>
                    <span className="text-[10px] text-[hsl(var(--content-placeholder))]">
                      {PROBLEM_CLASS_LABELS[entry.problemClass] ?? entry.problemClass} · {entry.language} · updated {relativeTime(entry.updatedAt)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-[hsl(var(--content-subtle))]">{entry.description}</p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                  <button
                    onClick={() => setOpenId(o => (o === entry.id ? null : entry.id))}
                    className="rounded-lg border border-[hsl(var(--border))] px-2.5 py-1 text-xs font-semibold"
                  >
                    {openId === entry.id ? 'Hide' : 'View'}
                  </button>
                  <button
                    onClick={() => copy(entry.id, entry.body)}
                    className="inline-flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] px-2.5 py-1 text-xs font-semibold"
                    aria-label={`Copy ${entry.name}`}
                  >
                    {copiedId === entry.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copiedId === entry.id ? 'Copied' : 'Copy'}
                  </button>
                  {canEdit && (
                    <>
                      <button
                        onClick={() => startEdit(entry)}
                        className="inline-flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] px-2.5 py-1 text-xs font-semibold"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      {entry.status !== 'approved' && (
                        <button
                          disabled={busyId === entry.id}
                          onClick={() => transition(entry, 'approve')}
                          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"
                        >
                          <CircleCheck className="h-3.5 w-3.5" /> Approve
                        </button>
                      )}
                      {entry.status !== 'disabled' && (
                        <button
                          disabled={busyId === entry.id}
                          onClick={() => transition(entry, 'disable')}
                          className="inline-flex items-center gap-1 rounded-lg border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 disabled:opacity-50 dark:text-red-400"
                        >
                          <CircleOff className="h-3.5 w-3.5" /> Disable
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {openId === entry.id && (
                <div className="mt-3" data-testid="script-detail">
                  {entry.parameters.length > 0 && (
                    <div className="mb-2 text-[11px] text-[hsl(var(--content-subtle))]">
                      Parameters:{' '}
                      {entry.parameters.map(p => (
                        <code key={p.name} className="mr-2 rounded bg-[hsl(var(--surface-subtle))] px-1 py-0.5" title={p.description}>
                          &lt;{p.name.toUpperCase()}&gt;{p.required ? ' *' : ''}{p.description ? ` - ${p.description}` : ''}
                        </code>
                      ))}
                    </div>
                  )}
                  <pre className="max-h-96 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-100" data-testid="script-body">
                    <code>{entry.body}</code>
                  </pre>
                  {entry.approvedBy && entry.approvedAt && (
                    <p className="mt-1 text-[10px] text-[hsl(var(--content-placeholder))]">
                      Approved {relativeTime(entry.approvedAt)}
                    </p>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {/* Add / edit form */}
      {form && canEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-label={form.id ? 'Edit script' : 'New script'}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold">{form.id ? `Edit: ${form.name}` : 'New script'}</h2>
                {form.id && (
                  <p className="text-xs text-[hsl(var(--content-subtle))]">
                    Saving creates a new version and returns the script to draft pending re-approval.
                  </p>
                )}
              </div>
              <button onClick={() => setForm(null)} aria-label="Close" className="rounded-lg p-1 hover:bg-[hsl(var(--surface-subtle))]">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <label className="block text-xs font-medium">
                Name
                <input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm"
                  placeholder="Disk space triage"
                />
              </label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className="block text-xs font-medium">
                  Problem class
                  <select
                    value={form.problemClass}
                    onChange={e => setForm({ ...form, problemClass: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-2 text-sm"
                  >
                    {PROBLEM_CLASSES.map(c => (
                      <option key={c} value={c}>{PROBLEM_CLASS_LABELS[c]}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-medium">
                  Classification
                  <select
                    value={form.classification}
                    onChange={e => setForm({ ...form, classification: e.target.value as ScriptClassification })}
                    className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-2 text-sm"
                    aria-label="Classification"
                  >
                    <option value="read_only">read-only (diagnostic)</option>
                    <option value="remediation">remediation (changes state)</option>
                  </select>
                </label>
                <label className="block text-xs font-medium">
                  Language
                  <input
                    value={form.language}
                    onChange={e => setForm({ ...form, language: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm"
                    placeholder="powershell"
                  />
                </label>
              </div>
              <label className="block text-xs font-medium">
                Description
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm"
                  placeholder="What this script shows and when to use it."
                />
              </label>
              <label className="block text-xs font-medium">
                Script body
                <textarea
                  value={form.body}
                  onChange={e => setForm({ ...form, body: e.target.value })}
                  rows={10}
                  spellCheck={false}
                  className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-slate-950 px-3 py-2 font-mono text-xs text-slate-100"
                  placeholder="Get-Volume | Format-Table DriveLetter, ..."
                  aria-label="Script body"
                />
              </label>
              {form.classification === 'read_only' && (
                <p className="text-[11px] text-[hsl(var(--content-subtle))]">
                  Read-only scripts are rejected server-side if they contain state-changing
                  commands (Remove-*, Stop-*, Restart-*, Clear-*, Set-* etc).
                </p>
              )}
              <label className="block text-xs font-medium">
                Parameters (JSON array)
                <textarea
                  value={form.parametersJson}
                  onChange={e => setForm({ ...form, parametersJson: e.target.value })}
                  rows={3}
                  spellCheck={false}
                  className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 font-mono text-xs"
                  aria-label="Parameters JSON"
                />
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setForm(null)} className="rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold">
                  Cancel
                </button>
                <button
                  disabled={saving || !form.name.trim() || !form.description.trim() || !form.body.trim()}
                  onClick={saveForm}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {saving ? 'Saving…' : form.id ? 'Save as new version' : 'Create draft'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
